import { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  Menu,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DoneAll,
  Launch,
  MarkEmailUnread,
  NotificationsNone,
  Visibility,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  CustomButton,
  DialogBody,
  DialogContainer,
  DialogTitle,
} from '../../common/components';
import {
  useMarkAllMyNotificationsRead,
  useMarkMyNotificationRead,
  useMarkMyNotificationUnread,
  useMyNotifications,
  useMyNotificationsUnreadCount,
} from '../../hooks/useNotifications';

const PROFILE_NOTIFICATIONS_ROUTE = '/perfil/notificaciones';

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('es-AR');
};

const getTargetPath = (notification) => {
  const payload = notification?.payload || {};
  return payload.frontend_path || payload.path || payload.contract_path || payload.project_path || null;
};

const getProfileNotificationPath = (notification) =>
  notification?.uuid
    ? `${PROFILE_NOTIFICATIONS_ROUTE}?notification=${notification.uuid}`
    : PROFILE_NOTIFICATIONS_ROUTE;

const NotificationsBell = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [detailNotification, setDetailNotification] = useState(null);
  const [unreadFirst, setUnreadFirst] = useState(true);
  const isOpen = Boolean(anchorEl);

  const { data: countData } = useMyNotificationsUnreadCount();
  const { data, isLoading } = useMyNotifications(
    { page: 1, per_page: 30 },
    { enabled: isOpen }
  );
  const markReadMutation = useMarkMyNotificationRead();
  const markUnreadMutation = useMarkMyNotificationUnread();
  const markAllReadMutation = useMarkAllMyNotificationsRead();

  const unreadCount = countData?.unread || 0;
  const notifications = useMemo(() => data?.results || [], [data?.results]);
  const visibleNotifications = useMemo(() => {
    if (!unreadFirst) return notifications;

    return [...notifications].sort((a, b) => {
      if (a.is_read === b.is_read) return 0;
      return a.is_read ? 1 : -1;
    });
  }, [notifications, unreadFirst]);
  const total = data?.total || notifications.length;

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewAll = () => {
    handleClose();
    navigate(PROFILE_NOTIFICATIONS_ROUTE);
  };

  const handleOpenTarget = async (notification) => {
    if (!notification?.is_read) {
      await markReadMutation.mutateAsync(notification.uuid);
    }

    handleClose();
    navigate(getTargetPath(notification) || getProfileNotificationPath(notification));
  };

  const handleToggleRead = async (event, notification) => {
    event.stopPropagation();
    if (notification.is_read) {
      await markUnreadMutation.mutateAsync(notification.uuid);
      return;
    }
    await markReadMutation.mutateAsync(notification.uuid);
  };

  const handleOpenDetail = (event, notification) => {
    event.stopPropagation();
    setDetailNotification(notification);
  };

  const handleOpenTargetAction = (event, notification) => {
    event.stopPropagation();
    handleOpenTarget(notification);
  };

  const handleMarkAllRead = async () => {
    await markAllReadMutation.mutateAsync({});
  };

  return (
    <>
      <Tooltip title="Notificaciones">
        <IconButton
          onClick={handleOpen}
          size="small"
          sx={{
            color: 'text.secondary',
            bgcolor: isOpen ? 'action.selected' : 'transparent',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Badge badgeContent={unreadCount} color="error" max={99} overlap="circular">
            <NotificationsNone />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: { xs: 350, sm: 500 },
            maxWidth: 'calc(100vw - 24px)',
            boxShadow: 'rgba(0, 0, 0, 0.12) 0px 8px 24px',
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Notificaciones
            </Typography>
            <Chip size="small" label={`${unreadCount} sin leer`} color="error" />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="No leídas primero">
              <Switch
                size="small"
                checked={unreadFirst}
                onChange={(event) => setUnreadFirst(event.target.checked)}
              />
            </Tooltip>
            <Button
              size="small"
              startIcon={<DoneAll />}
              onClick={handleMarkAllRead}
              disabled={!unreadCount || markAllReadMutation.isPending}
            >
              Marcar leídas
            </Button>
          </Stack>
        </Box>
        <Divider />

        {isLoading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No tenés notificaciones.
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ maxHeight: 520, overflowY: 'auto' }}>
            {visibleNotifications.map((notification) => (
              <Box
                key={notification.uuid}
                onClick={() => handleOpenTarget(notification)}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '8px minmax(0, 1fr) auto',
                  columnGap: 1.25,
                  px: 1.5,
                  py: 1.25,
                  cursor: 'pointer',
                  borderBottom: '1px solid',
                  borderBottomColor: 'divider',
                  bgcolor: notification.is_read ? 'background.paper' : 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' },
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: notification.is_read ? 'transparent' : 'primary.main',
                    alignSelf: 'flex-start',
                    transform: 'translateY(6px)',
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: notification.is_read ? 600 : 800,
                      color: 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {notification.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {notification.message}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {formatDateTime(notification.generated_at || notification.created_at)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.25} alignItems="flex-start">
                  <Tooltip title="Ver detalle">
                    <IconButton size="small" onClick={(event) => handleOpenDetail(event, notification)}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={notification.is_read ? 'Marcar no leída' : 'Marcar leída'}>
                    <IconButton size="small" onClick={(event) => handleToggleRead(event, notification)}>
                      {notification.is_read ? (
                        <MarkEmailUnread fontSize="small" />
                      ) : (
                        <DoneAll fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Abrir">
                    <IconButton
                      size="small"
                      onClick={(event) => handleOpenTargetAction(event, notification)}
                    >
                      <Launch fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            ))}
          </List>
        )}

        <Divider />
        <Box sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Mostrando {notifications.length} de {total}
          </Typography>
          <Button size="small" onClick={handleViewAll}>
            Ver todas
          </Button>
        </Box>
      </Menu>

      <DialogContainer
        open={Boolean(detailNotification)}
        onClose={() => setDetailNotification(null)}
        size="small"
        minHeight="auto"
      >
        <DialogTitle>Detalle de notificación</DialogTitle>
        <DialogBody sx={{ display: 'block' }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {detailNotification?.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {detailNotification?.message}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                size="small"
                label={detailNotification?.is_read ? 'Leída' : 'No leída'}
                color={detailNotification?.is_read ? 'default' : 'primary'}
              />
              {detailNotification?.contract_number && (
                <Chip size="small" label={`Contrato ${detailNotification.contract_number}`} />
              )}
              {detailNotification?.is_urgent && <Chip size="small" label="Urgente" color="error" />}
            </Stack>
            <Typography variant="caption" color="text.disabled">
              {formatDateTime(detailNotification?.generated_at || detailNotification?.created_at)}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <CustomButton variant="warning" onClick={() => setDetailNotification(null)}>
                Cerrar
              </CustomButton>
            </Stack>
          </Stack>
        </DialogBody>
      </DialogContainer>
    </>
  );
};

export default NotificationsBell;
